import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Buildings, CurrencyDollar, TrendUp, CalendarBlank, ChartLineUp, Receipt } from '@phosphor-icons/react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'PLN', symbol: 'zł' }
];

const PROPERTY_TYPES = [
  { id: 'residential', label: 'Residential' },
  { id: 'commercial', label: 'Commercial' }
];

const COLORS = {
  primary: '#FF6B35',
  secondary: '#F7931E',
  danger: '#ef4444',
  muted: '#94a3b8'
};

const RealEstateDashboard = () => {
  const [currency, setCurrency] = useState('USD');
  const [propertyType, setPropertyType] = useState('residential');
  const [formData, setFormData] = useState({
    propertySize: 100,
    pricePerSqm: 3000,
    downPaymentPercent: 20,
    interestRate: 4.5,
    mortgageTermYears: 25,
    rentCalculationType: 'per_sqm',
    rentValue: 15,
    taxRate: 20
  });
  
  const [results, setResults] = useState(null);

  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '$';

  const calculateInvestment = async () => {
    const propertySize = parseFloat(formData.propertySize);
    const pricePerSqm = parseFloat(formData.pricePerSqm);
    const downPaymentPercent = parseFloat(formData.downPaymentPercent);
    const interestRate = parseFloat(formData.interestRate);
    const mortgageTermYears = parseInt(formData.mortgageTermYears);
    const rentValue = parseFloat(formData.rentValue);
    const taxRate = parseFloat(formData.taxRate);
    
    if (isNaN(propertySize) || isNaN(pricePerSqm) || isNaN(downPaymentPercent) || 
        isNaN(interestRate) || isNaN(mortgageTermYears) || isNaN(rentValue) || isNaN(taxRate)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/calculate`, {
        currency: currency,
        property_size: propertySize,
        price_per_sqm: pricePerSqm,
        down_payment_percent: downPaymentPercent,
        interest_rate: interestRate,
        mortgage_term_years: mortgageTermYears,
        rent_calculation_type: formData.rentCalculationType,
        rent_value: rentValue,
        tax_rate: taxRate
      }, {
        withCredentials: true
      });
      setResults(response.data);
    } catch (error) {
      console.error('Calculation error:', error);
    }
  };

  useEffect(() => {
    calculateInvestment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const paymentBreakdownData = results ? [
    { name: 'Principal', value: results.loan_amount },
    { name: 'Interest', value: results.total_interest_paid },
    { name: 'Taxes', value: results.monthly_tax_on_rent * formData.mortgageTermYears * 12 }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437]">
      <div className="border-b border-white/10 px-4 sm:px-6 py-6 bg-[#0B1437]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl tracking-tight font-semibold text-white mb-1">Real Estate Analytics</h1>
          <p className="text-sm text-slate-400">Investment Calculator</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        >
          <div>
            <label className="text-xs font-medium uppercase text-slate-400 mb-3 block tracking-wider">Property Type</label>
            <div className="flex gap-3" data-testid="property-type-selector">
              {PROPERTY_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setPropertyType(type.id)}
                  className={`flex-1 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    propertyType === type.id
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF6B35]/80 text-white shadow-lg shadow-[#FF6B35]/20'
                      : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'
                  }`}
                  data-testid={`property-type-${type.id}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase text-slate-400 mb-3 block tracking-wider">Currency</label>
            <div className="grid grid-cols-4 gap-2" data-testid="currency-selector">
              {CURRENCIES.map((curr) => (
                <button
                  key={curr.code}
                  onClick={() => setCurrency(curr.code)}
                  className={`px-3 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                    currency === curr.code
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF6B35]/80 text-white shadow-lg shadow-[#FF6B35]/20'
                      : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'
                  }`}
                  data-testid={`currency-${curr.code}`}
                >
                  {curr.code}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-4 space-y-6"
          >
            <div className="card-premium rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <Buildings size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Property Details</h3>
              </div>
              <div className="space-y-4">
                <InputField
                  label="Size (sq m)"
                  value={formData.propertySize}
                  onChange={(e) => handleInputChange('propertySize', e.target.value)}
                  testId="input-property-size"
                />
                <InputField
                  label={`Price (${currencySymbol}/sq m)`}
                  value={formData.pricePerSqm}
                  onChange={(e) => handleInputChange('pricePerSqm', e.target.value)}
                  testId="input-price-per-sqm"
                />
              </div>
            </div>

            <div className="card-premium rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <CurrencyDollar size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Mortgage Details</h3>
              </div>
              <div className="space-y-4">
                <InputField
                  label="Down Payment (%)"
                  value={formData.downPaymentPercent}
                  onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)}
                  testId="input-down-payment"
                />
                <InputField
                  label="Interest Rate (%)"
                  value={formData.interestRate}
                  onChange={(e) => handleInputChange('interestRate', e.target.value)}
                  step="0.1"
                  testId="input-interest-rate"
                />
                <InputField
                  label="Term (years)"
                  value={formData.mortgageTermYears}
                  onChange={(e) => handleInputChange('mortgageTermYears', e.target.value)}
                  testId="input-mortgage-term"
                />
              </div>
            </div>

            <div className="card-premium rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <TrendUp size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Rental Income & Tax</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium uppercase text-slate-400 mb-2 block tracking-wider">Calculation Type</label>
                  <div className="flex gap-2" data-testid="rent-type-selector">
                    <button
                      onClick={() => handleInputChange('rentCalculationType', 'per_sqm')}
                      className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                        formData.rentCalculationType === 'per_sqm'
                          ? 'bg-[#FF6B35] text-white shadow-md'
                          : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'
                      }`}
                      data-testid="rent-type-per-sqm"
                    >
                      Per sq m
                    </button>
                    <button
                      onClick={() => handleInputChange('rentCalculationType', 'total')}
                      className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                        formData.rentCalculationType === 'total'
                          ? 'bg-[#FF6B35] text-white shadow-md'
                          : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'
                      }`}
                      data-testid="rent-type-total"
                    >
                      Total
                    </button>
                  </div>
                </div>
                <InputField
                  label="Rent Value"
                  value={formData.rentValue}
                  onChange={(e) => handleInputChange('rentValue', e.target.value)}
                  testId="input-rent-value"
                />
                <InputField
                  label="Tax Rate (%)"
                  value={formData.taxRate}
                  onChange={(e) => handleInputChange('taxRate', e.target.value)}
                  testId="input-tax-rate"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-8 space-y-6"
          >
            {results && (
              <>
                <div className="card-premium rounded-2xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">Investment Overview</h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <CompactMetric
                      label="Property Price"
                      value={formatCurrency(results.total_property_price)}
                      subtitle="Purchase"
                      positive={true}
                      testId="metric-property-price"
                    />
                    <CompactMetric
                      label="Final Value"
                      value={formatCurrency(results.total_property_price)}
                      subtitle="After Mortgage"
                      positive={true}
                      testId="metric-final-value"
                    />
                    <CompactMetric
                      label="Monthly Payment"
                      value={formatCurrency(results.monthly_mortgage_payment)}
                      subtitle="Mortgage"
                      positive={false}
                      testId="metric-mortgage-payment"
                    />
                    <CompactMetric
                      label="Cash Flow"
                      value={formatCurrency(Math.abs(results.monthly_cash_flow))}
                      subtitle={results.monthly_cash_flow >= 0 ? 'Positive' : 'Negative'}
                      positive={results.monthly_cash_flow >= 0}
                      testId="metric-cash-flow"
                    />
                    <CompactMetric
                      label="Coverage"
                      value={results.years_to_cover_mortgage ? `${results.years_to_cover_mortgage.toFixed(1)}y` : 'N/A'}
                      subtitle="Years to Cover"
                      positive={!!results.years_to_cover_mortgage}
                      testId="metric-years-to-cover"
                    />
                    <CompactMetric
                      label="ROI"
                      value={`${results.roi_percentage.toFixed(1)}%`}
                      subtitle="Return"
                      positive={results.roi_percentage > 0}
                      testId="metric-roi"
                    />
                    <CompactMetric
                      label="Net Income"
                      value={formatCurrency(results.monthly_net_income)}
                      subtitle="After Tax"
                      positive={results.monthly_net_income > 0}
                      testId="metric-net-income"
                    />
                    <CompactMetric
                      label="Interest"
                      value={formatCurrency(results.total_interest_paid)}
                      subtitle="Total"
                      positive={false}
                      testId="metric-interest"
                    />
                    <CompactMetric
                      label="Break-Even"
                      value={results.break_even_years ? `${results.break_even_years.toFixed(1)}y` : 'N/A'}
                      subtitle="Time"
                      positive={!!results.break_even_years}
                      testId="metric-breakeven"
                    />
                  </div>
                </div>

                <div className="card-premium rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <ChartLineUp size={20} weight="duotone" className="text-[#FF6B35]" />
                    <h3 className="text-base font-semibold text-white">Cash Flow Timeline</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={results.cash_flow_timeline}>
                      <defs>
                        <linearGradient id="colorCashFlow" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.25}/>
                          <stop offset="95%" stopColor="#FF6B35" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="year" 
                        stroke="#475569" 
                        style={{ fontSize: '11px' }}
                        tick={{ fill: '#94a3b8' }}
                      />
                      <YAxis 
                        stroke="#475569" 
                        style={{ fontSize: '11px' }}
                        tick={{ fill: '#94a3b8' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a2556', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="net" 
                        stroke="#FF6B35" 
                        strokeWidth={2.5}
                        fillOpacity={1} 
                        fill="url(#colorCashFlow)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-premium rounded-2xl p-6">
                    <h3 className="text-base font-semibold text-white mb-5">Payment Breakdown</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={paymentBreakdownData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {paymentBreakdownData.map((entry, index) => (
                            <Cell 
                              key={`pie-${entry.name}-${index}`} 
                              fill={[COLORS.primary, COLORS.secondary, COLORS.danger][index]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ 
                            backgroundColor: '#1a2556', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="card-premium rounded-2xl p-6">
                    <h3 className="text-base font-semibold text-white mb-5">Monthly Overview</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={[
                        { name: 'Rent', value: results.monthly_rent_income },
                        { name: 'Mortgage', value: results.monthly_mortgage_payment },
                        { name: 'Net', value: results.monthly_net_income }
                      ]}>
                        <XAxis 
                          dataKey="name" 
                          stroke="#475569" 
                          style={{ fontSize: '11px' }}
                          tick={{ fill: '#94a3b8' }}
                        />
                        <YAxis 
                          stroke="#475569" 
                          style={{ fontSize: '11px' }}
                          tick={{ fill: '#94a3b8' }}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ 
                            backgroundColor: '#1a2556', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          }}
                        />
                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                          {[
                            { index: 0, color: COLORS.primary },
                            { index: 1, color: COLORS.secondary },
                            { index: 2, color: COLORS.danger }
                          ].map((item) => (
                            <Cell 
                              key={`bar-${item.index}`} 
                              fill={item.color} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

const InputField = ({ label, value, onChange, step, testId }) => (
  <div>
    <label className="text-xs font-medium uppercase text-slate-400 mb-2 block tracking-wider">{label}</label>
    <input
      type="number"
      value={value}
      onChange={onChange}
      step={step}
      className="w-full bg-[#0B1437]/80 border border-white/10 rounded-xl h-11 px-4 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] transition-all duration-200"
      data-testid={testId}
    />
  </div>
);

const CompactMetric = ({ label, value, subtitle, positive, testId }) => (
  <div
    className="p-4 rounded-xl bg-[#1a2556]/60 backdrop-blur-xl border border-[#FF6B35]/20 hover:border-[#FF6B35]/40 transition-all duration-200"
    style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.1)' }}
    data-testid={testId}
  >
    <p className="text-xs font-medium uppercase text-slate-400 mb-1.5 tracking-wider truncate">{label}</p>
    <p 
      className="text-xl md:text-2xl font-mono font-bold text-white mb-1 tracking-tight break-all"
      style={{ fontFamily: 'JetBrains Mono, monospace' }}
    >
      {value}
    </p>
    <span className={`text-xs font-semibold truncate block ${
      positive ? 'text-[#FF6B35]' : 'text-[#ef4444]'
    }`}>
      {subtitle}
    </span>
  </div>
);

export default RealEstateDashboard;
